import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, User, Building2, CheckCircle2, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import OtpVerification from '../components/OtpVerification';
import axios from 'axios';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_praxis-launch/artifacts/9x9a538r_MeritumPraxis%20Logo.png";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Signup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get('type');           // 'candidate' | 'organization'
  const planParam = searchParams.get('plan');           // 'free' | 'professional'
  const cycleParam = searchParams.get('cycle');         // 'monthly' | 'annual'
  const [userType, setUserType] = useState(typeParam === 'organization' ? 'organization' : 'candidate');

  useEffect(() => {
    if (typeParam === 'organization' || typeParam === 'candidate') {
      setUserType(typeParam);
    }
  }, [typeParam]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    website: '', // honeypot - bots often auto-fill fields named 'website' or 'url'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [otpEmail, setOtpEmail] = useState('');
  const [otpPurpose, setOtpPurpose] = useState('');
  const formOpenedAt = useRef(Date.now());

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const routeAfterAuth = (user) => {
    if (user.user_type === 'organization') {
      navigate('/organization-dashboard');
    } else if (user.user_type === 'admin') {
      navigate('/admin-dashboard');
    } else if (planParam === 'professional') {
      navigate(`/plan-selection?plan=${cycleParam === 'annual' ? 'annual' : 'monthly'}`);
    } else {
      navigate('/candidate-dashboard');
    }
  };

  const handleOtpVerified = (payload) => {
    localStorage.setItem('user', JSON.stringify(payload.user));
    localStorage.setItem('token', payload.token);
    routeAfterAuth(payload.user);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    setIsSubmitting(true);
    setError('');

    try {
      const response = await axios.post(`${API}/auth/signup`, {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        user_type: userType,
        website: formData.website, // honeypot — should be empty
        form_open_ms: Date.now() - formOpenedAt.current,
      });

      // OTP required (new flow)
      if (response.data.status === 'otp_required') {
        setOtpEmail(response.data.email);
        setOtpPurpose(response.data.purpose || 'signup');
        return;
      }

      // Legacy path (grandfathered accounts) — auto-login and route
      if (response.data.id) {
        const loginResponse = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password
        });

        if (loginResponse.data.status === 'otp_required') {
          setOtpEmail(loginResponse.data.email);
          setOtpPurpose(loginResponse.data.purpose || 'login');
          return;
        }

        if (loginResponse.data.token) {
          localStorage.setItem('user', JSON.stringify(loginResponse.data.user));
          localStorage.setItem('token', loginResponse.data.token);
          routeAfterAuth(loginResponse.data.user);
        } else {
          setSuccess(true);
        }
      }
    } catch (err) {
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (Array.isArray(detail)) {
          setError(detail.map(d => d.msg).join('. '));
        } else {
          setError(detail);
        }
      } else {
        setError('Registration failed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (otpEmail) {
    return (
      <div data-testid="signup-otp-page" className="min-h-screen flex items-center justify-center bg-gradient-to-br from-meritum-navy via-meritum-teal to-meritum-teal p-6">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8">
          <OtpVerification
            email={otpEmail}
            purpose={otpPurpose}
            onVerified={handleOtpVerified}
            onCancel={() => { setOtpEmail(''); setOtpPurpose(''); }}
          />
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div data-testid="signup-success-page" className="min-h-screen flex items-center justify-center bg-gradient-to-br from-meritum-navy via-meritum-teal to-meritum-teal p-8">
        <div className="bg-white rounded-xl shadow-2xl p-12 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="text-green-600" size={40} />
          </div>
          <h1 className="font-heading text-3xl font-bold text-meritum-navy mb-4">
            Registration Successful!
          </h1>
          <p className="font-body text-slate-600 mb-8">
            Your account has been created. A notification has been sent to our team.
            You can now log in to your account.
          </p>
          <Link to="/login">
            <Button className="w-full bg-meritum-gold text-meritum-navy hover:bg-meritum-gold/90 font-body font-bold py-6 text-base">
              Go to Login
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="signup-page" className="min-h-screen flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex w-1/2 bg-gradient-to-br from-meritum-navy via-meritum-teal to-meritum-teal items-center justify-center p-12">
        <div className="text-center text-white max-w-md">
          <h2 className="font-heading text-4xl font-bold mb-6">
            Make Your Progress Visible
          </h2>
          <p className="font-body text-lg text-slate-200 leading-relaxed mb-8">
            Join MeritumPraxis and start building your verified professional profile today. 
            Connect with opportunities based on evidence, not just resumes.
          </p>
          <div className="space-y-4 text-left">
            <div className="flex items-center gap-4 bg-white/10 p-4 rounded-lg">
              <div className="w-10 h-10 bg-meritum-gold/20 rounded-full flex items-center justify-center">
                <User className="text-meritum-gold" size={20} />
              </div>
              <div>
                <p className="font-body text-sm text-white font-semibold">For Candidates</p>
                <p className="font-body text-xs text-slate-300">Build and verify your achievements</p>
              </div>
            </div>
            <div className="flex items-center gap-4 bg-white/10 p-4 rounded-lg">
              <div className="w-10 h-10 bg-meritum-gold/20 rounded-full flex items-center justify-center">
                <Building2 className="text-meritum-gold" size={20} />
              </div>
              <div>
                <p className="font-body text-sm text-white font-semibold">For Organizations</p>
                <p className="font-body text-xs text-slate-300">Discover evidence-based skill</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md text-center">
          {/* Back to Home / Previous Page */}
          <div className="flex items-center gap-4 mb-6">
            <button 
              onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/')} 
              className="inline-flex items-center gap-2 text-meritum-teal hover:text-meritum-navy font-body text-sm transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft size={16} />
              Back
            </button>
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-slate-400 hover:text-meritum-navy font-body text-sm transition-colors"
              data-testid="back-to-home"
            >
              Home
            </Link>
          </div>
          
          {/* Logo */}
          <div className="mb-8">
            <Link to="/" className="inline-block">
              <img
                src={LOGO_URL}
                alt="MeritumPraxis"
                className="h-20 w-auto object-contain mx-auto drop-shadow-lg"
              />
            </Link>
          </div>

          <h1 className="font-heading text-3xl md:text-4xl font-bold text-meritum-navy mb-3 text-left">
            Create Account
          </h1>
          <p className="font-body text-slate-600 mb-6 text-left">
            Start your journey with MeritumPraxis
          </p>

          {/* User Type Selection */}
          <div className="flex gap-4 mb-8">
            <button
              type="button"
              onClick={() => setUserType('candidate')}
              className={`flex-1 p-4 rounded-lg border-2 transition-all duration-200 ${
                userType === 'candidate'
                  ? 'border-meritum-gold bg-meritum-gold/5'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              data-testid="select-candidate-btn"
            >
              <User className={`mx-auto mb-2 ${userType === 'candidate' ? 'text-meritum-gold' : 'text-slate-400'}`} size={24} />
              <p className={`font-body text-sm font-medium ${userType === 'candidate' ? 'text-meritum-navy' : 'text-slate-600'}`}>
                Candidate
              </p>
            </button>
            <button
              type="button"
              onClick={() => setUserType('organization')}
              className={`flex-1 p-4 rounded-lg border-2 transition-all duration-200 ${
                userType === 'organization'
                  ? 'border-meritum-gold bg-meritum-gold/5'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              data-testid="select-organization-btn"
            >
              <Building2 className={`mx-auto mb-2 ${userType === 'organization' ? 'text-meritum-gold' : 'text-slate-400'}`} size={24} />
              <p className={`font-body text-sm font-medium ${userType === 'organization' ? 'text-meritum-navy' : 'text-slate-600'}`}>
                Organization
              </p>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="signup-form">
            {/* Anti-bot honeypot — hidden from real users, autofocus-blockers and bots often fill it */}
            <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
              <label htmlFor="website">Website (leave blank)</label>
              <input
                type="text"
                id="website"
                name="website"
                autoComplete="off"
                tabIndex={-1}
                value={formData.website}
                onChange={handleChange}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name" className="font-body text-sm font-medium text-meritum-navy">
                {userType === 'candidate' ? 'Full Name and Surname' : 'Organization Name'}
              </Label>
              <Input
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder={userType === 'candidate' ? 'Your full name and surname' : 'Organization name'}
                className="border-slate-300 focus:border-meritum-teal py-5"
                data-testid="signup-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="font-body text-sm font-medium text-meritum-navy">
                Email Address
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                className="border-slate-300 focus:border-meritum-teal py-5"
                data-testid="signup-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="font-body text-sm font-medium text-meritum-navy">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a password (min 6 characters)"
                  className="border-slate-300 focus:border-meritum-teal py-5 pr-12"
                  data-testid="signup-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="font-body text-sm font-medium text-meritum-navy">
                Confirm Password
              </Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
                className="border-slate-300 focus:border-meritum-teal py-5"
                data-testid="signup-confirm-password-input"
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-sm" data-testid="signup-error">
                <p className="font-body text-sm text-red-600">{error}</p>
              </div>
            )}

            <p className="font-body text-xs text-slate-500">
              By creating an account, you agree to our{' '}
              <Link to="/terms-of-use" className="text-meritum-teal hover:underline">
                Terms of Use
              </Link>{' '}
              and{' '}
              <Link to="/privacy-policy" className="text-meritum-teal hover:underline">
                Privacy Policy
              </Link>
            </p>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-meritum-gold text-meritum-navy hover:bg-meritum-gold/90 font-body font-bold py-6 text-base"
              data-testid="signup-submit-btn"
            >
              {isSubmitting ? 'Creating Account...' : 'Create Account'}
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </form>

          <p className="font-body text-center text-slate-600 mt-6">
            Already have an account?{' '}
            <Link
              to="/login"
              className="text-meritum-teal font-semibold hover:text-meritum-navy transition-colors"
              data-testid="login-link"
            >
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
